import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { GalleryCarousel } from "./index";
import SkeletonImage from "./common/SkeletonImage";

const SectionLayout = ({ section }) => {
  const strapiUrl = process.env.REACT_APP_STRAPI_URL || "http://156.67.104.166:1338";
  const layout = section.layout || "text-left-image-right";

  const renderContent = () => (
    <div>
      <h2 className="text-3xl font-bold text-[#0a1628] mb-4 tracking-tight">{section.title}</h2>
      {section.subtitle && (
        <h3 className="text-xl text-slate-600 mb-4">{section.subtitle}</h3>
      )}
      <div className="news-content mb-6 prose prose-green max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ node, src, alt, ...props }) => (
              <SkeletonImage
                src={src?.startsWith('/') ? `${strapiUrl}${src}` : src}
                alt={alt || section.title}
                className="w-full min-h-[200px] my-4"
                imgClassName="w-full h-auto rounded-lg shadow-lg"
                {...props}
              />
            ),
          }}
        >
          {section.content}
        </ReactMarkdown>
      </div>
      {section.ctaText && section.ctaLink && (
        <a
          href={section.ctaLink}
          className="inline-block bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors"
        >
          {section.ctaText}
        </a>
      )}
    </div>
  );

  const renderImage = (imageClass = "", { aspect = false } = {}) =>
    section.image && (
      <div className={imageClass}>
        <SkeletonImage
          src={`${strapiUrl}${section.image.url}`}
          alt={section.title || "Section image"}
          className={aspect ? "w-full aspect-[4/3]" : "w-full min-h-[200px]"}
          imgClassName={
            aspect
              ? "w-full h-full object-cover rounded-lg shadow-lg"
              : "w-full h-auto rounded-lg shadow-lg"
          }
        />
      </div>
    );

  switch (layout) {
    case "full-width-text":
      return (
        <div className="grid grid-cols-1 gap-8">
          <div className="max-w-4xl mx-auto w-full">
            {renderContent()}
          </div>
          {section.image && (
            <div className="w-full">
              {renderImage()}
            </div>
          )}
        </div>
      );

    case "full-width-image":
      return (
        <div className="grid grid-cols-1 gap-8">
          {section.image && (
            <div className="w-full">
              {renderImage()}
            </div>
          )}
          <div className="max-w-4xl mx-auto w-full">
            {renderContent()}
          </div>
        </div>
      );

    case "text-left-image-right":
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {renderContent()}
          {renderImage("", { aspect: true })}
        </div>
      );

    case "text-right-image-left":
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {renderImage("", { aspect: true })}
          {renderContent()}
        </div>
      );

    case "two-column-text":
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            {renderContent()}
          </div>
          {renderImage("", { aspect: true })}
        </div>
      );

    case "centered-content":
      return (
        <div className="grid grid-cols-1 gap-8">
          <div className="max-w-3xl mx-auto text-center">
            {renderContent()}
          </div>
          {section.image && (
            <div className="max-w-2xl mx-auto">
              {renderImage("")}
            </div>
          )}
        </div>
      );

    case "image-gallery-carousel":
      return (
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0a1628] mb-2 tracking-tight">{section.title}</h2>
            {section.subtitle && (
              <p className="text-xl text-slate-600 font-light">{section.subtitle}</p>
            )}
          </div>
          <GalleryCarousel 
            images={section.gallery || section.images || []} 
            strapiUrl={strapiUrl} 
          />
          {section.content && (
            <div className="mt-8 prose prose-green max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {section.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      );

    default:
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>{renderContent()}</div>
          {renderImage("", { aspect: true })}
        </div>
      );
  }
};

export default SectionLayout;
